// Relative imports (not `@/`) so bun can test this module.
import type { RuleSet } from '../rules/schema';
import { unitGlyph } from '../rules/ui';
import type { Unit } from '../sim';
import { fromMilli, unitHealRate, unitMaxHp, unitRank } from '../sim';
import { unitStrength } from '../sim/veterancy';
import { orderedResources, resourceIcon } from './resources';

/** A stat with what veterancy added to it (`bonus` 0 at rank 0 or for a unit type). */
export interface BoostedStat {
  value: number;
  bonus: number;
}

/** One unit type on a strong-vs / weak-vs line. */
export interface Matchup {
  type: string;
  name: string;
  glyph: string;
  /** Damage multiplier of the bonus. */
  mult: number;
}

/**
 * A strong-vs entry also says the damage per second it deals to that type:
 * nominal (strength × multiplier), before home defence and without a shot's
 * fire interval or accuracy.
 */
export interface Counter extends Matchup {
  kind: 'unit' | 'buildings';
  dps: number;
}

/** A number as the cards write it: at most two decimals, no trailing zeros. */
export function fmt(n: number): string {
  return String(Number(n.toFixed(2)));
}

export interface XpProgress {
  xp: number;
  rank: number;
  maxRank: number;
  /** Xp for rank 1, 2, … (`veterancy.ranks`). */
  thresholds: readonly number[];
  /** Xp of the next rank; null at the top. */
  next: number | null;
  /** Share of the bar filled, 0–1, over 0 … the top threshold. */
  fraction: number;
  /** What earns xp for this type: "damage dealt" 1, "buildings" 0.25, … */
  earns: { label: string; per: number }[];
}

export interface UnitStats {
  type: string;
  name: string;
  glyph: string;
  rank: number;
  /** Damage per second, rank included; null for a unit that doesn't attack. */
  damage: BoostedStat | null;
  maxHp: BoostedStat;
  /** The live unit's hp, when there is one. */
  hp: number | null;
  range: number;
  speed: number;
  sight: number;
  /** Resources per second while alive. */
  upkeep: Record<string, number>;
  /** What it may attack; null for a unit that doesn't attack. */
  targets: 'any' | 'units' | 'buildings' | null;
  mechanical: boolean;
  /** A healer's rate (rank included) and whom it mends. */
  heal: (BoostedStat & { whom: 'people and horses' | 'machines' }) | null;
  /** Shot kind of a ranged unit ("arrow"). */
  shot: string | null;
  strongVs: Counter[];
  /** Damage against buildings when it is above 1 (and it may hit buildings). */
  vsBuildings: Counter | null;
  /** Damage multiplier against buildings when it is below 1 (a tank ×0.5). */
  weakVsBuildings: number | null;
  weakVs: Matchup[];
  /** Null for a unit that doesn't rank up (workers, settlers, spies). */
  xp: XpProgress | null;
}

function matchup(rules: RuleSet, type: string, mult: number): Matchup {
  return { type, name: rules.units[type]?.name ?? type, glyph: unitGlyph(rules, type), mult };
}

function xpProgress(rules: RuleSet, type: string, unit?: Unit): XpProgress | null {
  const def = rules.units[type];
  if (!def?.military) return null;
  const v = rules.veterancy;
  const thresholds = v.ranks;
  const xp = unit?.xp ?? 0;
  const rank = unit ? unitRank(rules, unit) : 0;
  const top = thresholds[thresholds.length - 1] ?? 0;
  const earns: { label: string; per: number }[] = [];
  const attacks = def.strength > 0;
  if (attacks && def.targets !== 'buildings')
    earns.push({ label: 'damage dealt', per: v.xpPerDamage });
  if (attacks && def.targets !== 'units') {
    earns.push({ label: 'building damage', per: v.xpPerBuildingDamage ?? v.xpPerDamage });
  }
  if (def.healRate && (v.xpPerHeal ?? 0) > 0)
    earns.push({ label: 'hp healed', per: v.xpPerHeal ?? 0 });
  return {
    xp,
    rank,
    maxRank: thresholds.length,
    thresholds,
    next: thresholds[rank] ?? null,
    fraction: top > 0 ? Math.min(1, xp / top) : 0,
    earns,
  };
}

/**
 * What the UI shows of a unit type's stats: from the rules alone (base values,
 * rank 0), or for a live `unit` with its rank bonus (and the delta it adds),
 * hp and xp. Every number comes from the rules; nothing is hard-coded.
 */
export function unitStats(rules: RuleSet, type: string, unit?: Unit): UnitStats | null {
  const def = rules.units[type];
  if (!def) return null;
  const rank = unit ? unitRank(rules, unit) : 0;
  const boost = (base: number, live: number): BoostedStat => ({ value: live, bonus: live - base });
  // Mirrors unitStrength (sim/veterancy.ts) for a type with no live unit.
  const strength = unit
    ? unitStrength(rules, unit)
    : def.strength * (1 + rules.veterancy.strengthPerRank * rank);
  const maxHp = unit ? fromMilli(unitMaxHp(rules, unit)) : def.hp;
  const attacks = def.strength > 0;
  const hitsBuildings = attacks && def.targets !== 'units';

  const strongVs: Counter[] = Object.entries(def.bonusVs ?? {}).map(([t, mult]) => ({
    ...matchup(rules, t, mult),
    kind: 'unit',
    dps: strength * mult,
  }));
  const bvb = def.bonusVsBuildings ?? 1;
  const weakVs = Object.entries(rules.units)
    .filter(([id, other]) => id !== type && (other.bonusVs?.[type] ?? 1) > 1)
    .map(([id, other]) => matchup(rules, id, other.bonusVs?.[type] ?? 1));

  const upkeep: Record<string, number> = {};
  for (const res of orderedResources(def.upkeep ?? {})) {
    if ((def.upkeep?.[res] ?? 0) > 0) upkeep[res] = def.upkeep?.[res] ?? 0;
  }
  const healBase = def.healRate ?? 0;
  const healLive = unit ? unitHealRate(rules, unit) : healBase;

  return {
    type,
    name: def.name,
    glyph: unitGlyph(rules, type),
    rank,
    damage: attacks ? boost(def.strength, strength) : null,
    maxHp: boost(def.hp, maxHp),
    hp: unit ? fromMilli(unit.hp) : null,
    range: def.range,
    speed: def.speedMultiplier,
    sight: def.visionRadius,
    upkeep,
    targets: attacks ? (def.targets ?? 'any') : null,
    mechanical: !!def.mechanical,
    heal:
      healBase > 0
        ? {
            ...boost(healBase, healLive),
            whom: def.heals === 'mechanical' ? 'machines' : 'people and horses',
          }
        : null,
    shot: def.range > 1 ? (def.shot?.kind ?? null) : null,
    strongVs,
    vsBuildings:
      hitsBuildings && bvb > 1
        ? {
            type: 'buildings',
            name: 'Buildings',
            glyph: '',
            kind: 'buildings',
            mult: bvb,
            dps: strength * bvb,
          }
        : null,
    weakVsBuildings: hitsBuildings && bvb < 1 ? bvb : null,
    weakVs,
    xp: xpProgress(rules, type, unit),
  };
}

/** "0.05🌾/s" per upkeep resource, joined by spaces; '' when it costs nothing. */
export function upkeepText(upkeep: Readonly<Record<string, number>>): string {
  return orderedResources(upkeep)
    .map((res) => `${fmt(upkeep[res] ?? 0)}${resourceIcon(res)}/s`)
    .join(' ');
}

/** Short sentence for the targets row; null when "any" (nothing to flag). */
export function targetsText(stats: UnitStats): string | null {
  if (stats.targets === 'buildings') return 'Buildings only';
  if (stats.targets === 'units') return 'Units only';
  return null;
}

/** "72 xp to rank 3", or "Top rank" at the end. */
export function xpToNextText(xp: XpProgress): string {
  if (xp.next === null) return 'Top rank';
  return `${Math.ceil(xp.next - xp.xp)} xp to rank ${xp.rank + 1}`;
}
